import React, { useContext } from "react";
import { Button as AntdButton, Col, Row, ButtonProps as AntdButtonProps } from "antd";
import { RiFileEditLine } from "react-icons/ri";
import { useModal } from "react-modal-hook";
import { useTranslation } from "react-i18next";
import ProductContext from "../../../../context/product/context";
import FormHookModal from "../../modals/form-hook-modal";
import FormItem from "../../form-item";
import { Controller, useForm, FormProvider } from "react-hook-form";
import Input from "../../../form-components/input";
import { IQuantity } from "../../../../models/product/response";
import { IMatchQuantity } from "../../../../models/product/request"; // 👈 استيراد النوع الجديد

export interface ButtonProps extends AntdButtonProps {
  passedId: number;
  quantities: IQuantity[];
}

interface IFormValues {
  stores: {
    storeId: number;
    storeName: string | { ar: string; en: string };
    enteredQuantity: string; // نتركه string للـ form
    withdrawnQuantity: string; // نتركه string للـ form
  }[];
}

// Helper function to get display name from store_name
const getStoreName = (name: string | { ar: string; en: string }): string => {
  if (typeof name === 'string') return name;
  return name?.ar || name?.en || '-';
};

const MatchQuantityButton: React.FC<ButtonProps> = ({ passedId, quantities, ...props }) => {
  const { t } = useTranslation();
  const { actions, loading } = useContext(ProductContext);

  const [showFormModal, hideFormModal] = useModal(() => {
    const methods = useForm<IFormValues>({
      defaultValues: {
        stores: quantities.map((q) => ({
          storeId: q.store_id,
          storeName: q.store_name,
          enteredQuantity: q.entered_quantity?.toString() ?? "0",
          withdrawnQuantity: q.withdrawn_quantity?.toString() ?? "0",
        })),
      },
    });

    const { control, handleSubmit } = methods;

    const onSubmit = async (data: IFormValues) => {
      const payload: IMatchQuantity = {
        storeQuantities: data.stores.map((s) => ({
          store_id: s.storeId,
          entered_quantity: Number(s.enteredQuantity),
          withdrawn_quantity: Number(s.withdrawnQuantity),
        })),
      };

      await actions.matchQuantity(passedId, payload);
      actions.getData();
      hideFormModal();
    };

    return (
      <FormHookModal
        id="match-quantity-modal"
        confirmButtonText={t("match")}
        loading={loading.includes("matchQuantity")}
        title={t("match_quantity")}
        hideModal={hideFormModal}
        width="50vw"
        style={{ marginTop: "1.6rem", maxHeight: "70vh", overflowY: "auto" }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <FormProvider {...methods}>
          <form>
            {quantities.map((q, index) => (
              <Row key={q.store_id} gutter={[16, 8]} style={{ marginBottom: 8 }}>
                <Col span={12}>
                  <strong>{getStoreName(q.store_name)}</strong>
                  <FormItem label={t("entered_quantity")}>
                    <Controller
                      name={`stores.${index}.enteredQuantity`}
                      control={control}
                      rules={{
                        required: { value: true, message: t("field_is_required_message") },
                        validate: (value) => Number(value) > 0 || t("must_be_positive"),
                      }}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="number"
                          onChange={(e) => field.onChange(e.target.value)}
                        />
                      )}
                    />
                  </FormItem>
                </Col>
                <Col span={12}>
                  <FormItem label={t("withdrawn_quantity")}>
                    <Controller
                      name={`stores.${index}.withdrawnQuantity`}
                      control={control}
                      rules={{
                        required: { value: true, message: t("field_is_required_message") },
                        validate: (value) => Number(value) < 0 || t("must_be_negative"),
                      }}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="number"
                          onChange={(e) => field.onChange(e.target.value)}
                        />
                      )}
                    />
                  </FormItem>
                </Col>
              </Row>
            ))}
          </form>
        </FormProvider>
      </FormHookModal>
    );
  }, [passedId, quantities, loading]);

  return (
    <AntdButton type="primary" icon={<RiFileEditLine />} onClick={showFormModal} {...props} />
  );
};

export default MatchQuantityButton;
